import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { 
  MessageCircle, 
  Send, 
  X,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Building2,
  Filter,
  RefreshCw
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSupporto = () => {
  const [tickets, setTickets] = useState([]);
  const [ticketSelezionato, setTicketSelezionato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invioMessaggio, setInvioMessaggio] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Filtri
  const [filtroStato, setFiltroStato] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  
  // Form risposta
  const [nuovoMessaggio, setNuovoMessaggio] = useState('');
  
  const messaggiEndRef = useRef(null);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [filtroStato, filtroTipo]);

  useEffect(() => {
    // Scroll to bottom quando i messaggi cambiano
    if (messaggiEndRef.current) {
      messaggiEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketSelezionato?.messaggi]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      let url = `${API}/api/supporto/tickets?`;
      
      if (filtroStato) url += `stato=${filtroStato}&`;
      if (filtroTipo) url += `utente_tipo=${filtroTipo}&`;
      
      const response = await fetch(url);
      
      if (!response.ok) throw new Error('Errore nel caricamento ticket');
      
      const data = await response.json();
      setTickets(data);
      
      // Se c'è un ticket selezionato, aggiornalo
      if (ticketSelezionato) {
        const updated = data.find(t => t.id === ticketSelezionato.id);
        if (updated) setTicketSelezionato(updated);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Errore nel caricamento ticket');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API}/api/supporto/stats`);
      if (!response.ok) throw new Error('Errore nel caricamento statistiche');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const inviaMessaggio = async (e) => {
    e.preventDefault();
    
    if (!nuovoMessaggio.trim() || !ticketSelezionato) return;
    
    try {
      setInvioMessaggio(true);
      const response = await fetch(
        `${API}/api/supporto/ticket/${ticketSelezionato.id}/messaggio?autore_tipo=admin&autore_id=admin&autore_nome=Supporto InLeads`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ testo: nuovoMessaggio })
        }
      );
      
      if (!response.ok) throw new Error('Errore invio messaggio');
      
      const updated = await response.json();
      setTicketSelezionato(updated);
      setNuovoMessaggio('');
      
      await loadTickets();
      toast.success('Messaggio inviato');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Errore nell\'invio del messaggio');
    } finally {
      setInvioMessaggio(false);
    }
  };

  const cambiaStato = async (nuovoStato) => {
    if (!ticketSelezionato) return;
    
    try {
      const response = await fetch(
        `${API}/api/supporto/ticket/${ticketSelezionato.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ stato: nuovoStato })
        }
      );
      
      if (!response.ok) throw new Error('Errore aggiornamento stato');
      
      const updated = await response.json();
      setTicketSelezionato(updated);
      await loadTickets();
      await loadStats();
      toast.success('Stato aggiornato');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Errore nell\'aggiornamento stato');
    }
  };

  const getStatoBadge = (stato) => {
    const badges = {
      aperto: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, text: 'Aperto' },
      in_corso: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, text: 'In Corso' },
      risolto: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Risolto' },
      chiuso: { color: 'bg-gray-100 text-gray-700', icon: CheckCircle, text: 'Chiuso' }
    };
    
    const badge = badges[stato] || badges.aperto;
    const Icon = badge.icon;
    
    return (
      <Badge className={`${badge.color} border-0`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.text}
      </Badge>
    );
  };

  const formatData = (isoString) => {
    return new Date(isoString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Statistiche */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats.aperti}</p>
              <p className="text-sm text-gray-600">Aperti</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.in_corso}</p>
              <p className="text-sm text-gray-600">In Corso</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.risolti}</p>
              <p className="text-sm text-gray-600">Risolti</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-lg rounded-2xl">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-purple-600">{stats.total_tickets}</p>
              <p className="text-sm text-gray-600">Totali</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista Ticket */}
        <Card className="lg:col-span-1 border-0 shadow-lg rounded-2xl">
          <CardHeader>
            <div className="flex items-center justify-between mb-4">
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                Tutti i Ticket
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={loadTickets}
                className="rounded-xl"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Filtri */}
            <div className="space-y-2">
              <select
                value={filtroStato}
                onChange={(e) => setFiltroStato(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
              >
                <option value="">Tutti gli stati</option>
                <option value="aperto">Aperti</option>
                <option value="in_corso">In Corso</option>
                <option value="risolto">Risolti</option>
                <option value="chiuso">Chiusi</option>
              </select>
              
              <select
                value={filtroTipo}
                onChange={(e) => setFiltroTipo(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm"
              >
                <option value="">Tutti i tipi</option>
                <option value="candidato">Candidati</option>
                <option value="agenzia">Agenzie</option>
              </select>
            </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Caricamento...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Nessun ticket trovato</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    onClick={() => setTicketSelezionato(ticket)}
                    className={`p-4 rounded-xl cursor-pointer transition-all ${
                      ticketSelezionato?.id === ticket.id
                        ? 'bg-blue-50 border-2 border-blue-300'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {ticket.utente_tipo === 'candidato' ? (
                          <User className="w-4 h-4 text-blue-500" />
                        ) : (
                          <Building2 className="w-4 h-4 text-green-500" />
                        )}
                        <h4 className="font-semibold text-gray-900 text-sm">{ticket.oggetto}</h4>
                      </div>
                      {getStatoBadge(ticket.stato)}
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{ticket.utente_nome}</p>
                    <p className="text-xs text-gray-500 mb-2">
                      {formatData(ticket.data_ultimo_messaggio)}
                    </p>
                    <p className="text-xs text-gray-600">
                      {ticket.messaggi.length} messaggi
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversazione */}
        <Card className="lg:col-span-2 border-0 shadow-lg rounded-2xl">
          {ticketSelezionato ? (
            <>
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{ticketSelezionato.oggetto}</CardTitle>
                    <div className="flex items-center gap-2 mt-2">
                      {ticketSelezionato.utente_tipo === 'candidato' ? (
                        <Badge className="bg-blue-100 text-blue-700">
                          <User className="w-3 h-3 mr-1" />
                          Candidato
                        </Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">
                          <Building2 className="w-3 h-3 mr-1" />
                          Agenzia
                        </Badge>
                      )}
                      <span className="text-sm text-gray-600">{ticketSelezionato.utente_nome}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Creato: {formatData(ticketSelezionato.data_creazione)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatoBadge(ticketSelezionato.stato)}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTicketSelezionato(null)}
                      className="rounded-xl"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Azioni Rapide */}
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cambiaStato('in_corso')}
                    disabled={ticketSelezionato.stato === 'in_corso'}
                    className="rounded-xl"
                  >
                    In Corso
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cambiaStato('risolto')}
                    disabled={ticketSelezionato.stato === 'risolto'}
                    className="rounded-xl"
                  >
                    Risolto
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => cambiaStato('chiuso')}
                    disabled={ticketSelezionato.stato === 'chiuso'}
                    className="rounded-xl"
                  >
                    Chiudi
                  </Button>
                </div>
              </CardHeader>
              
              <CardContent className="p-0">
                {/* Messaggi */}
                <div className="h-[400px] overflow-y-auto p-6 space-y-4">
                  {ticketSelezionato.messaggi.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      className={`flex ${msg.autore_tipo === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%]`}>
                        <div className={`rounded-2xl p-4 ${
                          msg.autore_tipo === 'admin'
                            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                            : 'bg-gray-100'
                        }`}>
                          <p className="text-sm font-medium mb-1">
                            {msg.autore_nome}
                            {msg.autore_tipo === 'utente' && ' (Cliente)'}
                          </p>
                          <p className="text-sm">{msg.testo}</p>
                          <p className={`text-xs mt-2 ${msg.autore_tipo === 'admin' ? 'text-green-100' : 'text-gray-500'}`}>
                            {formatData(msg.data_invio)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messaggiEndRef} />
                </div>

                {/* Form Risposta */}
                {ticketSelezionato.stato !== 'chiuso' && (
                  <form onSubmit={inviaMessaggio} className="p-4 border-t bg-gray-50">
                    <div className="flex gap-2">
                      <Textarea
                        value={nuovoMessaggio}
                        onChange={(e) => setNuovoMessaggio(e.target.value)}
                        placeholder="Scrivi una risposta..."
                        className="flex-1 rounded-xl resize-none"
                        rows={2}
                        disabled={invioMessaggio}
                      />
                      <Button
                        type="submit"
                        disabled={invioMessaggio || !nuovoMessaggio.trim()}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl h-full"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="p-8 text-center">
              <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Seleziona un ticket per visualizzare la conversazione</p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminSupporto;
